import { useEffect, useState } from "react";
import api from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";

const STAGES = [
  ["applied", "Applied"],
  ["screened", "Screened"],
  ["round2_scheduled", "R2 Scheduled"],
  ["round2_feedback", "R2 Feedback"],
  ["final_scheduled", "Final"],
  ["final_feedback", "Panel"],
  ["selected", "Selected"],
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reqs, setReqs] = useState([]);

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data));
    api.get("/requisitions").then((r) => setReqs(r.data));
  }, []);

  const open = stats?.open_requisitions ?? 0;
  const pending = stats?.pending_requisitions ?? 0;
  const total = stats?.total_candidates ?? 0;
  const selected = stats?.selected ?? 0;

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="dashboard-page">
      <Header />

      <section className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 fade-up">
        <Metric label="Open Requisitions" value={open} testId="metric-open-reqs" />
        <Metric label="Pending HR Review" value={pending} testId="metric-pending-reqs" />
        <Metric label="Candidates In Pipeline" value={total} testId="metric-total-candidates" />
        <Metric label="Selected" value={selected} testId="metric-selected" />
      </section>

      <section className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6 fade-up fade-up-1">
        <div className="lg:col-span-2 bg-white border border-rule">
          <div className="px-5 py-4 border-b border-rule flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">
              Pipeline distribution
            </div>
            <Link to="/candidates" className="text-xs text-klein hover:underline" data-testid="link-pipeline">
              Open kanban →
            </Link>
          </div>
          <div className="p-5 space-y-3">
            {STAGES.map(([k, l]) => {
              const count = stats?.by_status?.[k] ?? 0;
              const max = Math.max(1, ...STAGES.map(([s]) => stats?.by_status?.[s] ?? 0));
              const w = (count / max) * 100;
              return (
                <div key={k} className="flex items-center gap-4">
                  <div className="w-32 font-mono text-[11px] uppercase tracking-wider text-quiet">{l}</div>
                  <div className="flex-1 h-3 bg-paper border border-rule">
                    <div className="h-full bg-ink" style={{ width: `${w}%` }} />
                  </div>
                  <div className="w-10 text-right font-mono text-sm text-ink">{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-rule">
          <div className="px-5 py-4 border-b border-rule flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Quick actions</div>
          </div>
          <div className="p-5 space-y-3">
            {user?.role === "manager" && (
              <Link
                to="/requisitions/new"
                data-testid="quick-raise-req"
                className="block bg-klein hover:bg-klein-hover text-white px-4 py-3 text-sm font-medium transition-colors"
              >
                Raise a Requisition →
              </Link>
            )}
            {user?.role === "hr" && (
              <Link
                to="/requisitions?status_filter=pending_review"
                data-testid="quick-review-queue"
                className="block bg-klein hover:bg-klein-hover text-white px-4 py-3 text-sm font-medium transition-colors"
              >
                Review Queue ({pending})  →
              </Link>
            )}
            <Link
              to="/candidates"
              data-testid="quick-pipeline"
              className="block border border-ink hover:bg-paper text-ink px-4 py-3 text-sm font-medium transition-colors"
            >
              Open Candidate Pipeline
            </Link>
            <Link
              to="/settings/email"
              data-testid="quick-email-setup"
              className="block border border-rule hover:border-ink text-quiet hover:text-ink px-4 py-3 text-sm transition-colors"
            >
              Configure outgoing email
            </Link>
          </div>
        </div>
      </section>

      <section className="mt-8 bg-white border border-rule fade-up fade-up-2">
        <div className="px-5 py-4 border-b border-rule flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">
            Recent requisitions
          </div>
          <Link to="/requisitions" className="text-xs text-klein hover:underline" data-testid="link-all-reqs">
            All requisitions →
          </Link>
        </div>
        <div className="divide-y divide-rule">
          {reqs.slice(0, 6).map((r) => (
            <Link
              key={r.id}
              to={`/requisitions/${r.id}`}
              className="flex items-center px-5 py-4 hover:bg-paper transition-colors"
              data-testid={`req-row-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-ink font-medium truncate">{r.title}</div>
                <div className="text-xs text-quiet font-mono mt-1">
                  {r.requisition_code || "—"} · {r.department} · {r.location}
                </div>
              </div>
              <div className="ml-4">
                <StatusBadge status={r.status} />
              </div>
            </Link>
          ))}
          {reqs.length === 0 && (
            <div className="px-5 py-12 text-center text-quiet text-sm">No requisitions yet.</div>
          )}
        </div>
      </section>
    </div>
  );
}

const Header = () => (
  <div className="flex items-end justify-between border-b border-rule pb-6">
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
        Operations · {new Date().toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "long" })}
      </div>
      <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Today's Hiring Floor.</h1>
    </div>
  </div>
);

const Metric = ({ label, value, testId }) => (
  <div className="bg-white border border-rule p-5" data-testid={testId}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{label}</div>
    <div className="font-mono text-4xl text-ink mt-3 tracking-tight">{String(value).padStart(2, "0")}</div>
  </div>
);
